import { Button, HSpacer, Text, TextLink, VSpacer } from '@/components/DesignSystem';
import { BookingStatusBadge } from '@/pages/CustomerDashboard/Bookings/BookingStatusBadge';
import { OrderDetailsModal } from '@/components/shared/OrderDetailsModal';
import {
  Collapse,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import { formatNumber } from '@shared/utilities';
import i18next from 'i18next';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ReservedPointsModal } from './ReservedPointsModal';
import { ApiSubwallet, ResolvedApiSubwalletBalance } from '@api/interfaces';
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

interface Order {
  date: string,
  id: string,
  label: string,
  erpId: string,
  pointsRedeemed: number,
}

interface Booking {
  id: string,
  status: 'ACTIVE' | 'EXPIRED' | 'CLOSED',
  bookingId: string,
  accountName: string,
  customerErpId: string,
  pointsReserved: number,
  pointsRedeemed: number,
  pointsRemaining: number,
  lastUpdated: string,
  bookingCreated: string,
  bookingExpires: string,
  orders: Order[],
  subwalletBalances?: ResolvedApiSubwalletBalance[],
  isClosed?: boolean | null,
  expirationDate?: string | null,
}

interface BookingsTableRowProps {
  booking: Booking,
}

const BookingsTableRow = ({ booking }: BookingsTableRowProps) => {
  const [open, setOpen] = useState(false);
  const [reservedPointsModalOpen, setReservedPointsModalOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const subwallets: ApiSubwallet[] = useMemo(() => (
    [
      ...new Map(
        booking.subwalletBalances?.map(({ subwallet }) => [subwallet.id, subwallet]),
      ).values(),
    ]
  ), [booking.subwalletBalances]);

  const transformedSubwallets = useMemo(() => {
    if (!booking.subwalletBalances) return [];

    return booking.subwalletBalances
      .filter((balance) => balance.points > 0)
      .map((balance) => ({
        description: balance.subwallet.redeemableProductsDescription || '',
        hasPromotions: (balance.subwallet.promotions?.length ?? 0) > 0,
        id: balance.subwallet.id,
        logo: balance.subwallet.manufacturer?.logo
          || balance.subwallet.retailer?.logomark
          || undefined,
        name: balance.subwallet.name,
        pointsRemaining: balance.points,
        redemptionSkus:
          balance.subwallet.skuProducts?.map((sku) => ({
            id: sku.id,
            sku: sku.externalId || sku.name,
            description: sku.name,
          })) ?? [],
      }));
  }, [booking.subwalletBalances]);

  const showReservedPointsButton = booking.pointsRemaining > 0;

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{
          cursor: 'pointer',
          '& > *': {
            backgroundColor: `${theme.palette.background.default} !important`,
            borderBottom: open ? 'none' : undefined,
          },
        }}
      >
        <TableCell sx={{ minWidth: 76, width: 76 }}>
          <Stack direction="row">
            <HSpacer size="3" />
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowRightIcon />}
          </Stack>
        </TableCell>
        <TableCell sx={{ minWidth: 104, width: 104 }}>
          <BookingStatusBadge
            bookingData={{
              isClosed: booking.isClosed ?? false,
              expirationDate: booking.expirationDate ? new Date(booking.expirationDate) : null,
            }}
          />
        </TableCell>
        <TableCell sx={{ minWidth: 120, width: 120, wordBreak: 'break-word' }}>
          <Text category="body-medium">{booking.bookingId}</Text>
        </TableCell>
        <TableCell sx={{ minWidth: 266, width: 266, wordBreak: 'break-word' }}>
          <Stack>
            <Text category="body-medium">{booking.accountName}</Text>
            <VSpacer size="1" />
            <Text category="body-small" sx={{ color: "#525252" }}>
              {t($ => $.common.labels.erpId, { id: booking.customerErpId })}
            </Text>
          </Stack>
        </TableCell>
        <TableCell sx={{ minWidth: 142, width: 142 }}>
          <Text category="body-medium">{formatNumber(booking.pointsReserved, i18next)}</Text>
        </TableCell>
        <TableCell sx={{ minWidth: 142, width: 142 }}>
          <Text category="body-medium">{formatNumber(booking.pointsRedeemed, i18next)}</Text>
        </TableCell>
        <TableCell sx={{ minWidth: 142, width: 142 }}>
          <Text category="body-medium">{formatNumber(booking.pointsRemaining, i18next)}</Text>
        </TableCell>
        <TableCell>
          {showReservedPointsButton && (
            <>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setReservedPointsModalOpen(true);
                }}
                size="small"
                square
                testID={`reserved-points-${booking.id}`}
                variant="contained"
              >
                {t($ => $.bookings.reservedPoints.title)}
              </Button>
              <ReservedPointsModal
                bookingId={booking.bookingId}
                onClose={() => setReservedPointsModalOpen(false)}
                open={reservedPointsModalOpen}
                subwallets={transformedSubwallets}
              />
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow
        sx={{
          '& > *': {
            backgroundColor: `${theme.palette.background.default} !important`,
          },
        }}
      >
        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }} sx={{
          borderBottom: open ? undefined : 'none',
        }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <VSpacer size="3" />
            <Stack direction="row">
              <Stack direction="row" sx={{ ml: '76px' }}>
                <Stack>
                  <Text category="body-small" color="secondary" noWrap>
                    {t($ => $.bookings.table.details.lastUpdated)}
                  </Text>
                  <Text category="body-medium">{booking.lastUpdated}</Text>
                  <VSpacer size="6" />
                  <Text category="body-small" color="secondary" noWrap>
                    {t($ => $.bookings.table.details.bookingCreated)}
                  </Text>
                  <Text category="body-medium" noWrap>{booking.bookingCreated}</Text>
                  <VSpacer size="6" />
                  <Text category="body-small" color="secondary" noWrap>
                    {t($ => $.bookings.table.details.bookingExpires)}
                  </Text>
                  <Text category="body-medium" noWrap>{booking.bookingExpires}</Text>
                </Stack>
                <HSpacer size="8" />
                <Stack>
                  <Stack direction="row">
                    <HSpacer size="3" />
                    <Text category="overline">
                      {t($ => $.bookings.table.details.ordersSection)}
                    </Text>
                  </Stack>
                  <VSpacer size="2" />
                  <Divider />
                  {booking.orders.length > 0 ? (
                    <TableContainer sx={{ padding: 0 }}>
                      <Table size="small">
                        <TableBody>
                          {booking.orders.map((order, index) => (
                            <TableRow key={index} sx={{
                              '& > *': {
                                backgroundColor: `${theme.palette.background.default} !important`,
                                padding: '8px',
                              },
                            }}>
                              <TableCell sx={{ pl: 0, width: '15%' }}>
                                <Text category="body-small" noWrap>{order.date}</Text>
                              </TableCell>
                              <TableCell sx={{ width: '40%' }}>
                                <Text category="body-small" noWrap>
                                  {order.label} {order.erpId}
                                </Text>
                              </TableCell>
                              <TableCell align="right">
                                {order.pointsRedeemed !== 0 && (
                                  <Text category="body-small" noWrap>
                                    {t($ => $.bookings.table.details.ptsRedeemed, {
                                      points: -formatNumber(order.pointsRedeemed, i18next),
                                    })}
                                  </Text>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ pr: 0 }}>
                                <TextLink
                                  noWrap
                                  onClick={() => setSelectedOrderId(order.id)}
                                  testID={`order-details-${order.erpId}`}
                                >
                                  <Text category="label-medium">
                                    {t($ => $.bookings.table.details.orderDetails)}
                                  </Text>
                                </TextLink>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Text category="body-medium" color="secondary" sx={{ padding: '8px' }}>
                      {t($ => $.bookings.table.details.noOrders)}
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Stack>
            <VSpacer size="7" />
          </Collapse>
        </TableCell>
      </TableRow>
      {!!selectedOrderId && (
        <OrderDetailsModal
          onClose={() => setSelectedOrderId(null)}
          orderId={selectedOrderId}
          subwallets={subwallets}
        />
      )}
    </>
  );
};

interface BookingsTableProps {
  bookings: Booking[],
}

export const BookingsTableHeader = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <TableHead sx={{
      position: 'sticky',
      '& .MuiTableCell-root': {
        borderBottom: `2px solid ${theme.palette.divider} !important`,
      },
      top: 0,
      zIndex: 1,
    }}>
      <TableRow>
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }} />
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }}>
          <Text category="title-small">{t($ => $.bookings.table.headers.status)}</Text>
        </TableCell>
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }}>
          <Text category="title-small">{t($ => $.bookings.table.headers.bookingId)}</Text>
        </TableCell>
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }}>
          <Text category="title-small">{t($ => $.common.customer)}</Text>
        </TableCell>
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }}>
          <Text category="title-small" noWrap>
            {t($ => $.bookings.table.headers.pointsReserved)}
          </Text>
        </TableCell>
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }}>
          <Text category="title-small" noWrap>
            {t($ => $.bookings.table.headers.pointsRedeemed)}
          </Text>
        </TableCell>
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }}>
          <Text category="title-small" noWrap>
            {t($ => $.bookings.table.headers.pointsRemaining)}
          </Text>
        </TableCell>
        <TableCell sx={{ backgroundColor: `${theme.palette.background.default} !important` }} />
      </TableRow>
    </TableHead>
  );
};

export const BookingsTableBody = ({ bookings }: BookingsTableProps) => {
  return (
    <TableBody>
      {bookings.map((booking) => (
        <BookingsTableRow booking={booking} key={booking.id} />
      ))}
    </TableBody>
  );
};
